import { Button } from "antd";
import { useCallback, useEffect, useState } from "react";
import FullscreenIcon from "../../../assets/icons/fullscreen.svg";
import ShareIcon from "../../../assets/icons/share.svg";
import ShareModalComponent from "../../../components/ShareModalComponent";
import { ILive } from "../../../helpers/types";
import { APP_DOMAIN } from "../../../utls/constants";
import "./index.less";

declare global {
  interface ScreenOrientation {
    lock(orientation: string): Promise<void>;
    unlock(): void;
  }
}

interface Props {
  id: string;
  live: ILive | null;
}

const isIOS = (): boolean => {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
};

export const FanLiveRTMPFooter = (props: Props) => {
  const { id, live } = props;
  const [isShareModalOpen, setIsShareModalOpen] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

  const enterFullscreen = useCallback(async () => {
    const container = document.querySelector(".rtmp-jvs") as HTMLElement;
    if (!container) return;

    if (!isIOS()) {
      // Android: native Fullscreen API on the container + orientation lock
      try {
        await container.requestFullscreen();
      } catch (e) {
        console.warn("requestFullscreen failed:", e);
      }
      try {
        await (window.screen.orientation as any).lock("landscape");
      } catch (e) {
        console.warn("orientation.lock failed:", e);
      }
      // Inject close button into the fullscreen container
      if (!container.querySelector(".android-fullscreen-close-btn")) {
        const btn = document.createElement("button");
        btn.className = "android-fullscreen-close-btn";
        btn.innerHTML = "✕";
        btn.setAttribute("aria-label", "Exit fullscreen");
        btn.onclick = () => exitFullscreen();
        container.appendChild(btn);
      }
    } else {
      // iOS: CSS-based pseudo-fullscreen
      container.classList.add("ios-pseudo-fullscreen");
      setIsFullscreen(true);
      // Nudge Safari to hide browser chrome (address bar / toolbar)
      setTimeout(() => window.scrollTo(0, 1), 50);
    }
  }, []);

  const exitFullscreen = useCallback(async () => {
    const container = document.querySelector(".rtmp-jvs") as HTMLElement;

    if (!isIOS()) {
      // Remove injected close button
      const btn = container?.querySelector(".android-fullscreen-close-btn");
      if (btn) btn.remove();
      if (document.fullscreenElement) {
        try {
          await document.exitFullscreen();
        } catch (e) {
          console.warn("exitFullscreen failed:", e);
        }
      }
      try {
        window.screen.orientation.unlock();
      } catch {}
    } else {
      // iOS: remove pseudo-fullscreen
      if (container) {
        container.classList.remove("ios-pseudo-fullscreen");
      }
      setIsFullscreen(false);
    }
  }, []);

  const handleFullscreen = useCallback(() => {
    if (isIOS()) {
      if (isFullscreen) {
        exitFullscreen();
      } else {
        enterFullscreen();
      }
    } else {
      if (document.fullscreenElement) {
        exitFullscreen();
      } else {
        enterFullscreen();
      }
    }
  }, [isFullscreen, enterFullscreen, exitFullscreen]);

  // Sync when Android exits fullscreen via back button / gesture
  useEffect(() => {
    const onFullscreenChange = () => {
      if (!document.fullscreenElement && !isIOS()) {
        // Clean up injected close button
        const btn = document.querySelector(".android-fullscreen-close-btn");
        if (btn) btn.remove();
        try {
          window.screen.orientation.unlock();
        } catch {}
      }
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Escape key exits iOS pseudo-fullscreen
  useEffect(() => {
    if (!isFullscreen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") exitFullscreen();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isFullscreen, exitFullscreen]);

  // Auto-fullscreen on device rotation
  useEffect(() => {
    if (!isIOS()) {
      // Android: listen to screen.orientation change
      const handleOrientationChange = () => {
        const type = window.screen.orientation?.type;
        if (type?.startsWith("landscape") && !document.fullscreenElement) {
          enterFullscreen();
        } else if (type?.startsWith("portrait") && document.fullscreenElement) {
          exitFullscreen();
        }
      };
      if (window.screen.orientation) {
        window.screen.orientation.addEventListener("change", handleOrientationChange);
      }
      return () => {
        if (window.screen.orientation) {
          window.screen.orientation.removeEventListener("change", handleOrientationChange);
        }
      };
    } else {
      // iOS: fallback to resize event
      const handleResize = () => {
        const isLandscape = window.innerWidth > window.innerHeight;
        const container = document.querySelector(".rtmp-jvs") as HTMLElement;
        if (!container) return;
        const isPseudoFS = container.classList.contains("ios-pseudo-fullscreen");
        if (isLandscape && !isPseudoFS) {
          enterFullscreen();
        } else if (!isLandscape && isPseudoFS) {
          exitFullscreen();
        }
      };
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }
  }, [enterFullscreen, exitFullscreen]);

  return (
    <>
      <div className="z-fan-live mb-5">
        <div className="fs-style-controls-container">
          <div className="fs-style-controls">
            <Button
              className="fullscreen-button"
              onClick={handleFullscreen}
              icon={<img src={FullscreenIcon} width={20} height={20} alt="fullscreen" />}
            />
            <Button
              className="fullscreen-button"
              onClick={() => setIsShareModalOpen(true)}
              icon={<img src={ShareIcon} width={20} height={20} alt="share" />}
            />
          </div>
        </div>
      </div>
      {isFullscreen && (
        <button className="ios-fullscreen-close-btn" onClick={exitFullscreen} aria-label="Exit fullscreen">
          ✕
        </button>
      )}
      <ShareModalComponent
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        id={live?.creator?._id}
        username={live?.creator?.username}
        link={`https://${APP_DOMAIN}/sebyvsstrongui`}
        headerTitle="share-stream-via"
      />
    </>
  );
};
